import i18n from 'i18next'
import { useAuthStore } from '@/stores/authStore'
import { capturePermissionSubject, usePermissionsStore } from '@/stores/permissionsStore'
import type { ScheduleAction, SchedulePatch } from '@/types/ltp-schedule'

export type ScheduleActionResult =
  | { ok: true; patch: SchedulePatch }
  | { ok: false; error: { code: string; message: string } }

/** Comparison and leveling proposals are pure reads, despite sharing a POST endpoint. */
export function canSendScheduleAction(action: ScheduleAction['action']): boolean {
  if (action === 'compare' || action === 'level_load') return true
  const permissions = usePermissionsStore.getState()
  if (permissions.isReadOnly()) return false
  if (action === 'add_intervention') return permissions.can('work_order', 'create')
  return permissions.can('maint_plan', action === 'save_version' ? 'create' : 'update')
}

export function assertScheduleActionAllowed(action: ScheduleAction['action']): void {
  if (!canSendScheduleAction(action))
    throw Object.assign(new Error(i18n.t('errors.permissionDenied')), { status: 403 })
}

/** One random actionId per user intent preserves the engine idempotency contract.
 * Typed writes bind to the originating owner and revalidate after token acquisition. */
export async function sendScheduleAction(
  input: ScheduleAction,
  send: (
    body: ScheduleAction & { actionId: string },
    write?: { owner: { tenantId: string; userId: string }; beforeSend: () => void },
  ) => Promise<ScheduleActionResult>,
): Promise<ScheduleActionResult> {
  const body = { actionId: crypto.randomUUID(), ...input }
  if (input.action === 'compare' || input.action === 'level_load') return send(body)
  assertScheduleActionAllowed(input.action)
  const { tenantId, userId } = useAuthStore.getState()
  return send(body, {
    owner: { tenantId, userId },
    beforeSend: () => assertScheduleActionAllowed(input.action),
  })
}

/** Retained callbacks must consult current canonical authority, not a render-time boolean. */
export function liveScheduleWrite<A extends unknown[], R>(
  callback: (...args: A) => R,
  action: ScheduleAction['action'] = 'update_task',
): (...args: A) => R | undefined {
  const { tenantId, userId } = useAuthStore.getState()
  const ownsSubject = capturePermissionSubject(tenantId, userId)
  return (...args) => {
    if (!ownsSubject()) return
    return canSendScheduleAction(action) ? callback(...args) : undefined
  }
}

/** Keep the public API export small while preserving its existing authenticated transport. */
export function createScheduleActionSender(
  request: (
    method: string,
    path: string,
    body: unknown,
    timeout?: number,
    headers?: Record<string, string>,
    owner?: { tenantId: string; userId: string },
    beforeSend?: () => void,
  ) => Promise<ScheduleActionResult>,
) {
  return (input: ScheduleAction) =>
    sendScheduleAction(input, (body, write) =>
      request(
        'POST',
        '/api/v1/planning/schedule/actions',
        body,
        undefined,
        undefined,
        write?.owner,
        write?.beforeSend,
      ),
    )
}
